/**
 * EventNotificationModal Component
 *
 * Professional modal for displaying detailed event information.
 * Shows once per event and can be triggered from banner.
 *
 * Features:
 * - Full event details with image
 * - Countdown timer
 * - "Don't show again" option
 * - Responsive design
 * - Professional styling
 */

import { getCountdownText, getDaysUntilEvent } from "@/config/events.config";
import { Event, EventType } from "@/types/events.types";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaCalendarDay,
  FaClock,
  FaExclamationCircle,
  FaTimes,
} from "react-icons/fa";

interface EventNotificationModalProps {
  event: Event;
  isOpen: boolean;
  onClose: () => void;
}

export const EventNotificationModal: React.FC<EventNotificationModalProps> = ({
  event,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState<string>("");
  const [daysRemaining, setDaysRemaining] = useState<number>(0);

  // Update countdown every minute
  useEffect(() => {
    if (!isOpen) return;

    const updateCountdown = () => {
      const days = getDaysUntilEvent(event.endDate);
      const countdownText = getCountdownText(event.endDate);
      setDaysRemaining(days);
      setCountdown(countdownText);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);

    return () => clearInterval(interval);
  }, [event.endDate, isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const isToday = daysRemaining === 0;
  const isPastEvent = daysRemaining < 0;

  // Get event type icon
  const getEventIcon = () => {
    switch (event.type) {
      case EventType.MEMORIAL:
        return FaCalendarDay;
      case EventType.ALERT:
        return FaExclamationCircle;
      default:
        return FaClock;
    }
  };

  const EventIcon = getEventIcon();

  return (
    <div className="event-modal-overlay" onClick={onClose}>
      <div
        className="event-modal-container"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="event-modal-header">
          <div className="flex items-center gap-3">
            <div className="event-modal-icon-wrapper">
              <EventIcon className="event-modal-icon" />
            </div>
            <h2 className="event-modal-title">{event.title}</h2>
          </div>
          <button
            onClick={onClose}
            className="event-modal-close"
            aria-label={t("common.close", "Close")}
          >
            <FaTimes />
          </button>
        </div>

        {/* Modal Body - Horizontal Layout */}
        <div className="event-modal-body-horizontal">
          {/* Left Side - Event Image */}
          {event.detailsImageUrl && (
            <div className="event-modal-image-section">
              <img
                src={event.detailsImageUrl}
                alt={event.title}
                className="event-modal-image-horizontal"
              />
            </div>
          )}

          {/* Right Side - Event Details */}
          <div className="event-modal-content-section">
            {/* Countdown Section */}
            {!isPastEvent && (
              <div className="event-modal-countdown-section">
                <div
                  className={`event-modal-countdown-card ${
                    isToday ? "event-today-card" : ""
                  }`}
                >
                  <div className="event-modal-countdown-content">
                    {isToday ? (
                      <>
                        <FaCalendarDay className="event-modal-countdown-icon pulse-animation" />
                        <div>
                          <p className="event-modal-countdown-label">
                            {t("events.eventToday", "आज का दिन है!")}
                          </p>
                          <p className="event-modal-countdown-value">
                            {new Date(event.endDate).toLocaleDateString(
                              "hi-IN",
                              {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                              }
                            )}
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        <FaClock className="event-modal-countdown-icon" />
                        <div>
                          <p className="event-modal-countdown-label">
                            {t("events.timeRemaining", "शेष समय")}
                          </p>
                          <p className="event-modal-countdown-value">
                            {countdown}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Event Description */}
            {event.description && (
              <div className="event-modal-description">
                <p className="event-modal-description-text">
                  {event.description}
                </p>
              </div>
            )}

            {/* Event Date */}
            <div className="event-modal-date-section">
              <div className="event-modal-date-card">
                <FaCalendarDay className="event-modal-date-icon" />
                <div>
                  <p className="event-modal-date-label">
                    {t("events.eventDate", "कार्यक्रम की तिथि")}
                  </p>
                  <p className="event-modal-date-value">
                    {new Date(event.endDate).toLocaleDateString("hi-IN", {
                      weekday: "long",
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </p>
                </div>
              </div>
            </div>

            {/* Memorial Message - Specific to Rajiv Dixit Event */}
            {event.type === EventType.MEMORIAL && (
              <div className="event-modal-memorial-message">
                <div className="event-modal-memorial-content">
                  <p className="text-lg font-semibold text-center mb-2">
                    🙏 श्रद्धांजलि 🙏
                  </p>
                  <p className="text-center text-gray-700 leading-relaxed">
                    स्वदेशी विचारक स्व. राजीव दीक्षित जी की जन्म एवं पुण्यतिथि
                    पर नारी सुरक्षा सेवा व्यवस्था परिवार के सभी सदस्य उन्हें
                    श्रद्धांजलि अर्पित करते हैं।
                  </p>
                </div>
              </div>
            )}

            {/* Footer inside content section */}
            <div className="event-modal-footer-inline">
              <button onClick={onClose} className="event-modal-btn-primary">
                {t("common.close", "बंद करें")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventNotificationModal;
